import { useEffect, useMemo, useState } from 'react';
import useDashboardData from './useDashboardData';

export const USER_SEARCH_MIN_LENGTH = 2;
const USER_SEARCH_DEBOUNCE_MS = 300;
const USER_SEARCH_LIMIT = 10;

function useDebouncedValue(value, delayMs) {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setDebounced(value), delayMs);
        return () => clearTimeout(t);
    }, [value, delayMs]);
    return debounced;
}

export function userDisplayName(r) {
    const first = r.firstName || '';
    const last = r.lastName || '';
    return first ? `${first[0]}. ${last}`.trim() : (r.email || '').toLowerCase();
}

export default function useUserSearch(query, { enabled = true, scope = null } = {}) {
    const trimmed = (query || '').trim();
    const term = useDebouncedValue(trimmed, USER_SEARCH_DEBOUNCE_MS);
    const active = enabled && term.length >= USER_SEARCH_MIN_LENGTH;
    const request = useMemo(
        () => ({
            metric: 'users',
            aggregation: 'user_search',
            search: term,
            limit: USER_SEARCH_LIMIT,
            ...(scope ? { scope } : {}),
        }),
        [term, scope]
    );
    const { currentData, isFetching } = useDashboardData(request, { skip: !active });
    const records = useMemo(() => (active ? currentData?.records || [] : []), [currentData, active]);
    const pending = enabled && trimmed.length >= USER_SEARCH_MIN_LENGTH && (trimmed !== term || isFetching);
    return { records, pending };
}
